import apiClient from "../../services/ApiClientService";
import { dataLoading } from "../decorators/LoadingAction.decorator";
import { DateTimeFormatStrings } from "../enums";
import { CalculationResult } from "../models/CalculationResult";
import { FormulaType } from "../models/FormulaType";
import { FormulaVersion } from "../models/FormulaVersion";
import { GroupedFiscalPeriod } from "../models/GroupedFiscalPeriod";
import { Utilities } from "../utils/utilities";

export class FormulaApi {
  @dataLoading(undefined)
  static async getFormulaVersions(formulaTypeId: string) {
    if (Utilities.isNullOrEmptyString(formulaTypeId)) {
      return [];
    }

    var result = await apiClient.get(`/api/formula/versions?formulaTypeId=${formulaTypeId}`);
    if (result.status === 200) {
      return (result.data as FormulaVersion[]).sort((a, b) => Utilities.compareBasic(a.version, b.version, true));
    } else {
      return undefined;
    }
  }

  @dataLoading(undefined)
  static async getFormula(formulaId: string) {
    if (Utilities.isNullOrEmptyString(formulaId)) {
      return undefined;
    }

    var result = await apiClient.get(`/api/formula/version?formulaId=${formulaId}`);
    if (result.status === 200) {
      return result.data;
    } else {
      return undefined;
    }
  }

  @dataLoading(undefined)
  static async getProductionDeterminants(fiscalPeriodId: string, maxFiscalPeriodsBack: string) {
    if (Utilities.isNullOrEmptyString(fiscalPeriodId)) {
      return undefined;
    }

    var asOf = encodeURIComponent(Utilities.formatDate(new Date(), DateTimeFormatStrings.FORMAT_9));
    var url = `/api/production/productionDeterminantsForFiscalPeriod?fiscalPeriodId=${fiscalPeriodId}&asOf=${asOf}&maxFiscalPeriodsBack=${maxFiscalPeriodsBack}`;
    var result = await apiClient.get(url);
    if (result.status === 200) {
      return result.data;
    } else {
      return undefined;
    }
  }

  @dataLoading(undefined)
  static async updateFormulaSimulate(fiscalPeriodId: string, formulaId: string, customerId: string, testFormula: string) {
    var res = await apiClient.post("/api/formula/simulate", {
      FiscalPeriodId: fiscalPeriodId,
      FormulaId: formulaId,
      CustomerId: customerId,
      TestFormula: testFormula,
    });
    return res.data as CalculationResult;
  }

  @dataLoading(false)
  static async saveNewVersion(formulaTypeId: string, maxFiscalPeriodsBack: string, calculationString: string, comment: string, productLines: string) {
    await apiClient.post("/api/formula/saveNewVersion", {
      FormulaTypeId: formulaTypeId,
      MaxFiscalPeriodsBack: maxFiscalPeriodsBack,
      CalculationString: calculationString,
      IsValid: true,
      IsDraft: true,
      Comment: comment,
      ProductLines: productLines,
    });
    return true;
  }

  @dataLoading(false)
  static async createNewFormulaType(
    newFormulaTypeName: string,
    selectedSurchargeType: string,
    selectedFormulaTypeid: string,
    maxFiscalPeriodsBack: string,
    calculationString: string,
    comment: string,
    productLines: string
  ) {
    await apiClient.post("/api/formula/createNewFormulaType", {
      Name: newFormulaTypeName,
      SurchargeType: selectedSurchargeType,
      FormulaTypeId: selectedFormulaTypeid,
      MaxFiscalPeriodsBack: maxFiscalPeriodsBack,
      CalculationString: calculationString,
      IsValid: true,
      IsDraft: true,
      Comment: comment,
      ProductLines: productLines,
    });
    return true;
  }
}

export const tryLoadingFormulaConfig = async () => {
  return (await Utilities.tryServiceCallsWithLoading(
    (error) => {
      alert("Error in get formula config, see console log for details");
      console.log(error);
      return undefined;
    },
    getFormulaTypes,
    getFiscalMonths
  )) as [FormulaType[], GroupedFiscalPeriod[]];
};

export const getFormulaTypes = async () => {
  var result = await apiClient.get("/api/formula/types");
  if (result.status === 200) {
    const utcNow = new Date();
    return (result.data as FormulaType[])
      .filter((item) => {
        const formulaFrom = new Date(item.effectiveFrom);
        const formulaTo = new Date(item.effectiveTo);
        return utcNow >= formulaFrom && utcNow <= formulaTo;
      })
      .sort((a, b) => Utilities.compareBasic(a.name, b.name, true));
  } else {
    return undefined;
  }
};

export const getFiscalMonths = async () => {
  var result = await apiClient.get("/api/fiscalperiod/listGroupedCurrentFY");
  if (result.status === 200) {
    return result.data as GroupedFiscalPeriod[];
  } else {
    return undefined;
  }
};
